import React from 'react'
import intl from 'react-intl-universal'
import quillEmoji from 'quill-emoji'
import classNames from 'classnames'
import { showModal } from './CustomModal'
import { showPopover, hidePopover } from './Popover'
import { tokenize, untokenize } from '../Tokenize'
import 'quill-emoji/dist/quill-emoji.css'
import { stripHtml } from 'utils/functions'
import { Button } from './Button'
import AiTextGeneration from './AiTextGeneration'
import { BASE_API_URL, GNOWBE_SOURCE } from 'constants/General'
import { getAuthTokens } from 'actions/utils'
import { track } from 'utils/track'
import GnowbeAiIcon from 'components/addons/GnowbeAiIcon'

interface EditorProps {
  id: string;
  defaultText: string;
  field: string;
  placeholder: string;
  debounce?: number;
  maxLength?: number;
  showReadingTime?: boolean;
  preventNewLine?: boolean;
  additionalFormats?: string[];
  removeFormats?: string[];
  isConnected?: boolean;
  readonly?: boolean;
  showAiGenerateText?: boolean;
  courseId: string;
  chapterId?: string;
  actionId?: string;
  userId?: string;
  organizationId: string;
  workspaceCanImproveText?: boolean

  onFocus?: (field: string) => void;
  onChange?: (field: string, value: string) => void;
  onBlur?: (field: string) => void;
}

export default class Editor extends React.Component<EditorProps, { selected: boolean, text: string, loaded: boolean, generateHover: boolean }> {
  private _debounce
  private _formats
  private _modules
  private _quill
  private _isMounted
  private ReactQuill = window['ReactQuill']

  constructor(props) {
    super(props)

    const text = tokenize(props.defaultText)
    this.state = { selected: false, text: text || '', loaded: false, generateHover: false }

    this._formats = this.defaultFormats()
    this._modules = this.defaultModules()

    if (props.additionalFormats) {
      this._modules.toolbar.container = this._modules.toolbar.container.concat([this.props.additionalFormats])
      this._formats = this._formats.concat(this.props.additionalFormats)
    }

    this._modules.toolbar.container = this._modules.toolbar.container.concat([['clean']])

    this.onFocus = this.onFocus.bind(this)
    this.onChange = this.onChange.bind(this)
    this.onBlur = this.onBlur.bind(this)
  }
  token = getAuthTokens()

  async componentDidMount() {
    document.addEventListener('mouseover', this.showPopover)
    document.addEventListener('mouseout', this.hidePopover)
    this._isMounted = true

    if (!this.state.loaded) {
      if (!this.ReactQuill) {
        const rq = await import('react-quill')
        this.ReactQuill = rq.default
        console.log('Quill wasn\'t loaded, loading now.')
      }

      //#region Quill
      const Parchment = this.ReactQuill.Quill.import('parchment')

      // change default block to <div> (instead of <p>)
      const Block = this.ReactQuill.Quill.import('blots/block')
      Block.tagName = 'DIV'
      this.ReactQuill.Quill.register(Block, true)

      // register an <abbr> element for {{explain}}
      const Inline: any = this.ReactQuill.Quill.import('blots/inline')
      class AbbrBlot extends Inline {
        static create(value) {
          const node = super.create()
          node.setAttribute('title', value)
          return node
        }
        static formats(node) {
          return node.getAttribute('title')
        }
      }
      AbbrBlot.blotName = 'abbr'
      AbbrBlot.tagName = 'abbr'
      this.ReactQuill.Quill.register(AbbrBlot, true)

      // https://github.com/quilljs/quill/issues/2096#issuecomment-399576957
      class CustomColor extends Inline {
        constructor(domNode, value) {
          super(domNode, value)
          domNode.style.color = domNode.color
          const span = this.replaceWith(new Inline(Inline.create()))
          span.children.forEach((child) => {
            if (child.attributes) child.attributes.copy(span)
            if (child.unwrap) child.unwrap()
          })
          this.remove()
          return span
        }
      }
      CustomColor.blotName = 'customColor'
      CustomColor.tagName = 'FONT'
      this.ReactQuill.Quill.register(CustomColor, true)

      // change icon to button
      const icons = this.ReactQuill.Quill.import('ui/icons')
      icons['blockquote'] = '<img src="/images/icon-chat.svg" style="height: 16px; display: flex; align-items: center;" />'
      icons['clean'] = '<img src="/images/icon-remove-format.svg" style="height: 16px; display: flex; align-items: center;" />'
      //#endregion

      const { EmojiBlot, ShortNameEmoji, ToolbarEmoji, TextAreaEmoji } = quillEmoji
      this.ReactQuill.Quill.register({
        'formats/emoji': EmojiBlot,
        'modules/emoji-shortname': ShortNameEmoji,
        'modules/emoji-toolbar': ToolbarEmoji,
        'modules/emoji-textarea': TextAreaEmoji,
      }, true)

      // const Delta = this.ReactQuill.Quill.import('delta')
      // const Clipboard: any = this.ReactQuill.Quill.import('modules/clipboard')
      // class PlainClipboard extends Clipboard {
      //   onPaste(e) {
      //     e.preventDefault()
      //     const range = this.quill.getSelection()
      //     const text = e.clipboardData.getData('text/plain')
      //     const delta = new Delta()
      //       .retain(range.index)
      //       .delete(range.length)
      //       .insert(text)
      //     const index = text.length + range.index
      //     const length = 0
      //     this.quill.updateContents(delta, 'silent')
      //     this.quill.setSelection(index, length, 'silent')
      //     this.quill.scrollIntoView()
      //   }
      // }
      // this.ReactQuill.Quill.register('modules/clipboard', PlainClipboard, true)

      this._isMounted && this.setState({ loaded: true })
    }
  }

  componentWillUnmount() {
    document.removeEventListener('mouseover', this.showPopover)
    document.removeEventListener('mouseout', this.hidePopover)
    this._isMounted = false
  }

  showPopover(e) {
    const explainerTooltip = document.getElementById('explainer-popover')
    const cleanTooltip = document.getElementById('clean-popover')
    if (e.target.className === 'ql-blockquote' && !explainerTooltip) {
      showPopover(e.target, intl.get('editor_inline_explanation'), undefined, 'gnowbe-popover', 'explainer-popover')
    }
    if (e.target.className === 'ql-clean' && !cleanTooltip) {
      showPopover(e.target, intl.get('editor_clean_formatting'), undefined, 'gnowbe-popover', 'clean-popover')
    }
  }

  hidePopover(e) {
    const tooltip = document.getElementById('explainer-popover')
    const cleanTooltip = document.getElementById('clean-popover')
    if (tooltip && e.target && e.target.className !== 'ql-blockquote' && e.relatedTarget && e.relatedTarget.className !== 'ql-blockquote') {
      hidePopover('explainer-popover')
    }
    if (cleanTooltip && e.target && e.target.className !== 'ql-clean' && e.relatedTarget && e.relatedTarget.className !== 'ql-clean') {
      hidePopover('clean-popover')
    }
  }

  onFocus() {
    this._isMounted && this.setState({ selected: true })
    this.props.onFocus && this.props.onFocus(this.props.field)
  }

  onChange(value: string) {
    const { maxLength, field, debounce, onChange } = this.props
    this._isMounted && this.setState({ text: value }, () => {
      if (maxLength && value.length > maxLength) {
        return
      }

      if (debounce) {
        this._debounce = clearTimeout(this._debounce)
        this._debounce = setTimeout(() => {
          const text = untokenize(this.state.text)
          onChange && onChange(field, text || '')
        }, debounce)
      }
      else {
        const text = untokenize(this.state.text)
        onChange && onChange(field, text || '')
      }
    })
    // replace bogus {{ with '' and reset caret position
    // let caretPosition
    // let replacedString
    //     if (/(({{)\s)|({{\w+\s)/.exec(value)) {
    //       caretPosition = this_quill.getSelection().index
    //       replacedString = value.match(/(({{)\s)|({{\w+\s)/)[0]
    //       value = value.replace(/(({{)\s)|({{\w+\s)/, '')
    //     }
    //   }
    //   value = value.replace(/<abbr title="(.*)">(.*)<\/abbr>/g, ($0, $1, $2) => `{{explain:${encodeURIComponent($2)}:${encodeURIComponent($1)}}}`)
    // }
    // if (caretPosition) {
    //   this._quill.setSelection(caretPosition + 1)
    // }
  }

  onBlur() {
    this._isMounted && this.setState({ selected: false })
    this.props.onBlur && this.props.onBlur(this.props.field)
  }

  defaultFormats() {
    let formats = ['bold', 'italic', 'underline', 'script', 'list', 'link', 'abbr', 'indent', 'emoji', 'color', 'clean']
    const removeFormats = this.props.removeFormats
    if (removeFormats && removeFormats.length > 0) {
      formats = formats.filter(el => !removeFormats.includes(el))
    }
    return formats
  }

  defaultModules() {
    const removeFormats = this.props.removeFormats
    const container = [
      ['bold', 'italic', 'underline', { color: [] }],
      ['link', 'emoji'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      (!removeFormats || !removeFormats.includes('indent')) && [{ indent: '-1'}, { indent: '+1' }] || undefined,
      (!removeFormats || !removeFormats.includes('sub')) && [{ script: 'sub' }, { script: 'super' }] || undefined,
    ]
    return {
      history: { userOnly: true },
      'emoji-toolbar': true,
      // 'emoji-textarea': true,
      'emoji-shortname': true,
      toolbar: {
        container,
        handlers: {
          emoji: () => {},
          blockquote: () => {
            const editor = this._quill.getEditor()
            const range = editor.getSelection()

            const leaf = editor.getLeaf(range.index)[0]
            const index = editor.getIndex(leaf)
            const title = leaf.domNode.parentElement.title

            let value = ''

            // get title at cursor position
            if (title) {
              editor.setSelection(index, leaf.length())
              value = title
            }

            // get title for selected range
            if (range.length > 0 && value === '') {
              const ops = editor.getContents(range.index, range.length)
              const filtered = ops.filter(op => op.attributes)
              if (filtered.length > 0) {
                editor.setSelection(range.index, range.length)
                value = filtered[0].attributes.abbr
              }
            }

            showModal({
              value,
              title: intl.get('editor_explanation_title'),
              content: intl.get('editor_explanation_description'),
              prompt: true,
              primaryText: intl.get('global_add'),
              primaryAction: (r) => {
                const s = stripHtml(r)
                editor.format('abbr', s)
                setTimeout(() => {
                  editor.setSelection(range.index + range.length)
                }, 250)
              },
              onHideModal: () => {
                setTimeout(() => {
                  editor.setSelection(range.index + range.length)
                }, 250)
              },
            })
          },
        },
      },
    }
  }

  calculateReadingTime(text) {
    const words = text.replace(/(<\/?[^>]+>)/g, ' ').split(/\s+/).filter(s => s !== '')
    const time = Math.round(words.length / 3)
    const minutes = Math.floor(time / 60)
    const seconds = time - minutes * 60
    const minutesString = (minutes ? `<strong>${minutes}</strong> ${intl.get('minutes', { minutes })}` : '')
    const secondsString = (minutes && seconds > 0 ? ' and ' : '') + (seconds ? `<strong>${seconds}</strong> ${(intl.get('seconds', { seconds }))}` : (!minutes ? `<strong>0</strong> ${(intl.get('seconds', { seconds }))}` : ''))
    return minutesString + secondsString
  }

  resetBotRequest = async () => {
    const apiUrl = `${BASE_API_URL}/api/v1/integrations/bot/resetBotConversation/managedbots/ai_content_copilot/${this.props.userId}-${this.props.actionId ? this.props.actionId : this.props.chapterId}`
    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${this.token}`,
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'x-gnowbe-source': GNOWBE_SOURCE,
          'OrganizationId': this.props.organizationId,
        },
      });
      if (!response.ok) {
        throw new Error('Network response was not ok')
      }
    } catch (error) {
      console.error('Error fetching data:', error)
    }
  }

  handleGenerateClick = () => {
    track({
      event: 'Ai Text Generator Icon Clicked',
      variables: {
        programId: this.props.courseId,
        sessionId: this.props.chapterId || '',
        actionId: this.props.actionId || '',
        onScreen: `${this.props.actionId ? 'Action' : this.props.chapterId ? 'Session' : 'Program'}`,
      },
    })
    const modal = showModal({
      title: <div className="flex flex-row justify-center items-center">
        <GnowbeAiIcon pathFill="#ED8054" width="24" height="24" />
        <div className="font-medium text-2xl ml-2">GnowbeAI Text Generation Tool</div>
      </div>,
      component: <AiTextGeneration onChange={this.onChange} closeModal={() => modal.close()} courseId={this.props.courseId} chapterId={this.props.chapterId} actionId={this.props.actionId} userId={this.props.userId} existingDesc={this.state.text} organizationId={this.props.organizationId} workspaceCanImproveText={this.props.workspaceCanImproveText} />,
      hideFooter: true,
      onHideModal: () => this.resetBotRequest(),
    })
  }

  render() {
    const text = this._quill && this._quill.getEditor().getText() || this.state.text
    const cn = classNames({
      error: this.props.maxLength && this.state.text && this.state.text.length > this.props.maxLength,
      disabled: this.props.readonly,
      'active ring-1 ring-blue-300 border-blue-300': this.state.selected && !this.props.readonly,
      'border-team': !(this.state.selected && !this.props.readonly),
      'rounded-md': true,
      'block border w-full shadow-sm sm:text-sm': true,
    })
    const homeMain = document.getElementById('home-main')
    const learnMain = document.getElementById('learn-main')

    return (
      <>
        {this.state.loaded
          ? <div className="relative">
            <this.ReactQuill
                ref={ref => this._quill = ref}
                className={cn}
                value={this.state.text}
                placeholder={this.props.placeholder}
                scrollingContainer={homeMain || learnMain}
                readOnly={!this.props.isConnected || this.props.readonly}
                modules={this._modules}
                formats={this._formats}
                onFocus={this.onFocus}
                onChange={this.onChange}
                onBlur={this.onBlur}
              />
              {this.props.showAiGenerateText && <div onMouseEnter={() => this.setState({ generateHover: true })} onMouseLeave={() => this.setState({ generateHover: false })}>
                <Button className={`absolute bottom-3 right-3 bg-[#ED8054] text-white ${this.state.generateHover && 'py-1 pr-3 pl-0'} rounded-3xl px-0 h-8 border-none focus:ring-[#ED8054]`}
                  svgIcon={<svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="16" cy="16" r="15.5" fill="#ED8054" stroke="#ED8054"/>
                      <g clipPath="url(#clip0_0_1000)">
                        <path d="M15.6142 14.0279C15.7379 13.6566 16.2622 13.6566 16.3859 14.0279L17.1115 16.207C17.2712 16.686 17.5403 17.1213 17.8975 17.4782C18.2547 17.8352 18.69 18.104 19.1692 18.2635L21.3472 18.9891C21.7184 19.1129 21.7184 19.6371 21.3472 19.7609L19.168 20.4865C18.689 20.6462 18.2538 20.9153 17.8968 21.2725C17.5398 21.6296 17.271 22.065 17.1115 22.5441L16.3859 24.7221C16.3592 24.8034 16.3076 24.8742 16.2383 24.9244C16.169 24.9746 16.0856 25.0016 16 25.0016C15.9145 25.0016 15.8311 24.9746 15.7618 24.9244C15.6925 24.8742 15.6408 24.8034 15.6142 24.7221L14.8885 22.543C14.7289 22.0641 14.46 21.6289 14.103 21.272C13.7461 20.915 13.3109 20.6461 12.832 20.4865L10.6529 19.7609C10.5716 19.7342 10.5008 19.6825 10.4506 19.6132C10.4004 19.5439 10.3734 19.4606 10.3734 19.375C10.3734 19.2894 10.4004 19.2061 10.4506 19.1368C10.5008 19.0675 10.5716 19.0158 10.6529 18.9891L12.832 18.2635C13.3109 18.1039 13.7461 17.835 14.103 17.478C14.46 17.1211 14.7289 16.6859 14.8885 16.207L15.6142 14.0279ZM11.2683 8.29149C11.2844 8.24277 11.3155 8.20038 11.3571 8.17033C11.3987 8.14028 11.4487 8.12411 11.5 8.12411C11.5514 8.12411 11.6014 8.14028 11.643 8.17033C11.6846 8.20038 11.7156 8.24277 11.7318 8.29149L12.1672 9.59874C12.3618 10.1815 12.8185 10.6382 13.4013 10.8329L14.7085 11.2682C14.7573 11.2844 14.7996 11.3155 14.8297 11.3571C14.8597 11.3987 14.8759 11.4487 14.8759 11.5C14.8759 11.5513 14.8597 11.6013 14.8297 11.6429C14.7996 11.6845 14.7573 11.7156 14.7085 11.7317L13.4013 12.1671C13.1139 12.2629 12.8527 12.4243 12.6385 12.6385C12.4243 12.8527 12.2629 13.1138 12.1672 13.4012L11.7318 14.7085C11.7156 14.7572 11.6846 14.7996 11.643 14.8297C11.6014 14.8597 11.5514 14.8759 11.5 14.8759C11.4487 14.8759 11.3987 14.8597 11.3571 14.8297C11.3155 14.7996 11.2844 14.7572 11.2683 14.7085L10.8329 13.4012C10.7371 13.1138 10.5758 12.8527 10.3616 12.6385C10.1473 12.4243 9.88619 12.2629 9.59878 12.1671L8.29153 11.7317C8.24281 11.7156 8.20042 11.6845 8.17037 11.6429C8.14032 11.6013 8.12415 11.5513 8.12415 11.5C8.12415 11.4487 8.14032 11.3987 8.17037 11.3571C8.20042 11.3155 8.24281 11.2844 8.29153 11.2682L9.59878 10.8329C9.88619 10.7371 10.1473 10.5757 10.3616 10.3615C10.5758 10.1473 10.7371 9.88615 10.8329 9.59874L11.2683 8.29149ZM19.2209 7.11194C19.2209 7.11156 19.221 7.11119 19.2211 7.11083C19.2322 7.07902 19.253 7.05145 19.2804 7.0319C19.3081 7.01224 19.3411 7.00168 19.375 7.00168C19.4089 7.00168 19.442 7.01224 19.4696 7.0319C19.4972 7.05156 19.5181 7.07934 19.5292 7.11137L19.8194 7.98212C19.9488 8.37137 20.2537 8.67624 20.6429 8.80562L21.5137 9.09587C21.5457 9.10697 21.5735 9.12778 21.5931 9.15541C21.6128 9.18303 21.6233 9.21609 21.6233 9.24999C21.6233 9.2839 21.6128 9.31696 21.5931 9.34458C21.5735 9.3722 21.5457 9.39301 21.5137 9.40412L20.6429 9.69437C20.4511 9.75818 20.2768 9.86584 20.1338 10.0088C19.9909 10.1517 19.8832 10.326 19.8194 10.5179L19.5292 11.3886C19.5181 11.4207 19.4972 11.4484 19.4696 11.4681C19.442 11.4877 19.4089 11.4983 19.375 11.4983C19.3411 11.4983 19.3081 11.4877 19.2804 11.4681C19.2528 11.4484 19.232 11.4207 19.2209 11.3886L18.9307 10.5179C18.8668 10.326 18.7592 10.1517 18.6162 10.0088C18.4733 9.86584 18.299 9.75818 18.1072 9.69437L17.2375 9.40412C17.2055 9.39301 17.1777 9.3722 17.1581 9.34458C17.1384 9.31696 17.1278 9.2839 17.1278 9.24999C17.1278 9.21609 17.1384 9.18303 17.1581 9.15541C17.1777 9.12778 17.2055 9.10697 17.2375 9.09587L18.1083 8.80562C18.4975 8.67624 18.8024 8.37137 18.9318 7.98212L19.2207 7.11302C19.2208 7.11267 19.2209 7.11231 19.2209 7.11194Z" fill="white"/>
                      </g>
                      <defs>
                        <clipPath id="clip0_0_1000">
                          <rect width="18" height="18" fill="white" transform="translate(7 7)"/>
                        </clipPath>
                      </defs>
                    </svg>}
                  text={this.state.generateHover ? 'Generate' : ''}
                  onClick={this.handleGenerateClick} />
              </div>}
          </div>
          : <div>Loading the editor...</div>
        }

        {this.props.showReadingTime
          ? <div className="mt-4">{intl.get('editor_reading_time')} <span dangerouslySetInnerHTML={{ __html: this.calculateReadingTime(text) }} /></div>
          : null
        }

        {this.props.maxLength && this._quill && this.state.text.length >= this.props.maxLength - Math.round(this.props.maxLength * 0.1)
          ? this.props.maxLength > this.state.text.length
            ? <div className="mt-5 px-4 py-3 rounded-md bg-orange-100">
                You have <span className="text-green-600">{this.props.maxLength - this.state.text.length}</span>/{this.props.maxLength} character{this.props.maxLength - this.state.text.length === 1 ? '' : 's'} left.
              </div>
            : this.props.maxLength === this.state.text.length
              ? <div className="mt-5 px-4 py-3 rounded-md bg-green-100">
                  You have <span className="text-orange-600">0</span>/{this.props.maxLength} characters left.
                </div>
              : <div className="mt-5 px-4 py-3 rounded-md bg-red-100">
                  You are <span className="text-red-600">{Math.abs(this.props.maxLength - this.state.text.length)}</span> character{Math.abs(this.props.maxLength - this._quill.getEditor().getText().length) === 1 ? '' : 's'} over the limit ({this.props.maxLength} characters).<br/>
                  <strong className="text-sm">Text that goes over the limit might not be saved (or displayed on mobile)!</strong>
                </div>
          : null
        }
      </>
    )
  }
}
